import React, { useEffect, useRef, useState } from "react";
import { Amplify } from "aws-amplify";
import { confirmSignIn, signIn } from "aws-amplify/auth";
import {
  Alert,
  Box,
  Button,
  Card,
  CircularProgress,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import {
  ArrowForwardRounded,
  CheckCircleOutline,
  LockOutlined,
  PersonOutline,
} from "@mui/icons-material";
import { DEVELOPER_AUTH_CONFIG } from "../config/aws-config";
import {
  checkDeveloperCallbackRelay,
  checkDeveloperPairingRelay,
  saveDeveloperAuthRelay,
} from "./developer-auth-service";
import {
  clearDeveloperBrowserSession,
  getDeveloperRelayAuthState,
} from "./developer-auth-session";

const SIX_DIGITS = /^\d{6}$/;
const DEVELOPER_CALLBACK_INTENT_KEY =
  "mhome.pampas.developer.callback-relay.v1";
const CALLBACK_INTENT_TTL_MILLIS = 10 * 60 * 1000;

export default function DeveloperLogin() {
  const query = new URLSearchParams(window.location.search);
  const flow = query.get("flow");
  const relayId = query.get("id");
  const [loginTicket, setLoginTicket] = useState("");
  const validRequest =
    Boolean(relayId) && (flow === "pairing" || flow === "callback");
  const [phase, setPhase] = useState(
    flow === "callback" ? "validating" : "pairing"
  );
  const [clientDisplayName, setClientDisplayName] = useState("");
  const [pairingCode, setPairingCode] = useState("");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [challengeValue, setChallengeValue] = useState("");
  const [deliveryDestination, setDeliveryDestination] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const emailMfaCompleted = useRef(false);

  useEffect(() => {
    Amplify.configure(DEVELOPER_AUTH_CONFIG);
    if (flow !== "callback" || !relayId) return;
    const fragment = new URLSearchParams(window.location.hash.slice(1));
    let ticket = fragment.get("ticket") || "";
    if (ticket) {
      sessionStorage.setItem(
        DEVELOPER_CALLBACK_INTENT_KEY,
        JSON.stringify({ relayId, loginTicket: ticket, createdAt: Date.now() })
      );
      window.history.replaceState(
        null,
        "",
        `${window.location.pathname}${window.location.search}`
      );
    } else {
      try {
        const stored = JSON.parse(
          sessionStorage.getItem(DEVELOPER_CALLBACK_INTENT_KEY) || "{}"
        );
        if (
          stored.relayId === relayId &&
          typeof stored.loginTicket === "string" &&
          Number(stored.createdAt) + CALLBACK_INTENT_TTL_MILLIS > Date.now()
        ) {
          ticket = stored.loginTicket;
        }
      } catch (_) {
        ticket = "";
      }
    }
    if (!ticket) {
      sessionStorage.removeItem(DEVELOPER_CALLBACK_INTENT_KEY);
      setError("This developer login request is invalid or expired.");
      setPhase("invalid");
      return;
    }
    setLoginTicket(ticket);
    checkDeveloperCallbackRelay(relayId, ticket)
      .then((result) => {
        setClientDisplayName(
          result.clientDisplayName || "the requesting application"
        );
        setPhase("credentials");
      })
      .catch((relayError) => {
        setError(
          relayError?.message ||
            "This developer login request is invalid or expired."
        );
        setPhase("invalid");
      });
  }, [flow, relayId]);

  const finish = async () => {
    if (!emailMfaCompleted.current) {
      throw new Error("Email verification is required for developer sign-in.");
    }
    const authState = await getDeveloperRelayAuthState();
    const delivery = await saveDeveloperAuthRelay({
      relayId,
      ...(flow === "callback"
        ? { flow: "callback", loginTicket }
        : { flow: "pairing", pairingCode }),
      ...authState,
    });
    if (flow === "callback" && !delivery.redirectUri) {
      throw new Error("Missing developer login callback.");
    }
    if (flow === "callback") {
      sessionStorage.removeItem(DEVELOPER_CALLBACK_INTENT_KEY);
    }
    // The CLI owns the relayed session. Keep no developer token copy in the
    // browser after delivery, and do not revoke the CLI refresh token.
    try {
      await clearDeveloperBrowserSession();
    } catch (_) {
      // Delivery already succeeded. A local cleanup failure must not cause a
      // second relay delivery or report the completed login as failed.
    }
    if (flow === "callback") {
      window.location.replace(delivery.redirectUri);
      return;
    }
    setPhase("complete");
  };

  const advanceSignIn = async (result) => {
    const nextStep = result?.nextStep?.signInStep;
    if (result?.isSignedIn || nextStep === "DONE") {
      await finish();
      return;
    }

    if (nextStep === "CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED") {
      setChallengeValue("");
      setPhase("new-password");
      return;
    }

    if (nextStep === "CONFIRM_SIGN_IN_WITH_EMAIL_CODE") {
      setDeliveryDestination(
        result.nextStep?.codeDeliveryDetails?.destination || "your email"
      );
      setChallengeValue("");
      setPhase("email-code");
      return;
    }

    if (
      nextStep === "CONTINUE_SIGN_IN_WITH_MFA_SELECTION" ||
      nextStep === "CONTINUE_SIGN_IN_WITH_MFA_SETUP_SELECTION"
    ) {
      const allowed = result.nextStep?.allowedMFATypes || [];
      if (!allowed.includes("EMAIL")) {
        throw new Error("Email verification is not enabled for this account.");
      }
      await advanceSignIn(await confirmSignIn({ challengeResponse: "EMAIL" }));
      return;
    }

    if (nextStep === "CONTINUE_SIGN_IN_WITH_EMAIL_SETUP") {
      setChallengeValue("");
      setPhase("email-setup");
      return;
    }

    throw new Error(
      "This account is not configured for developer email verification."
    );
  };

  const verifyPairingCode = async (event) => {
    event.preventDefault();
    if (!SIX_DIGITS.test(pairingCode)) {
      setError("Enter the 6-digit Meow pairing code shown in the terminal.");
      return;
    }
    setBusy(true);
    setError("");
    try {
      await checkDeveloperPairingRelay(relayId, pairingCode);
      setPhase("credentials");
    } catch (relayError) {
      setError(relayError?.message || "Unable to verify the pairing code.");
    } finally {
      setBusy(false);
    }
  };

  const submitCredentials = async (event) => {
    event.preventDefault();
    setBusy(true);
    setError("");
    emailMfaCompleted.current = false;
    try {
      // Clear only the browser copy for the developer app client. This forces a
      // fresh password + email MFA challenge without revoking Meow sessions.
      await clearDeveloperBrowserSession();
      const result = await signIn({
        username: username.trim(),
        password,
        options: { authFlowType: "USER_SRP_AUTH" },
      });
      await advanceSignIn(result);
    } catch (signInError) {
      setError(signInError?.message || "Unable to sign in.");
    } finally {
      setBusy(false);
    }
  };

  const submitChallenge = async (event) => {
    event.preventDefault();
    if (phase === "email-code" && !SIX_DIGITS.test(challengeValue)) {
      setError("Enter the 6-digit code sent to your developer email.");
      return;
    }
    setBusy(true);
    setError("");
    try {
      const result = await confirmSignIn({
        challengeResponse: challengeValue.trim(),
      });
      if (phase === "email-code") emailMfaCompleted.current = true;
      await advanceSignIn(result);
    } catch (challengeError) {
      if (emailMfaCompleted.current) setPhase("delivery-error");
      setError(challengeError?.message || "Unable to complete sign-in.");
    } finally {
      setBusy(false);
    }
  };

  const retryDelivery = async () => {
    setBusy(true);
    setError("");
    try {
      await finish();
    } catch (deliveryError) {
      setError(
        deliveryError?.message ||
          "Unable to send the developer session to Meow."
      );
    } finally {
      setBusy(false);
    }
  };

  if (!validRequest) {
    return (
      <DeveloperShell>
        <Alert severity="error">
          Start developer login from a supported mhome.ai client.
        </Alert>
      </DeveloperShell>
    );
  }

  if (phase === "complete") {
    return (
      <DeveloperShell>
        <Stack spacing={2.5} alignItems="center" textAlign="center">
          <CheckCircleOutline sx={{ color: "#63d7a5", fontSize: 72 }} />
          <Typography variant="h4">Developer access granted</Typography>
          <Typography color="rgba(255,255,255,0.7)">
            The developer session was delivered to Meow. You can close this
            window.
          </Typography>
        </Stack>
      </DeveloperShell>
    );
  }

  return (
    <DeveloperShell>
      <Stack spacing={3.5}>
        <Box>
          <Typography
            variant="overline"
            sx={{
              color: "#73d6a6",
              fontFamily:
                "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
              fontSize: 11,
              fontWeight: 650,
              letterSpacing: "0.14em",
            }}
          >
            Developer authentication
          </Typography>
          <Typography
            component="h1"
            sx={{
              mt: 1,
              color: "#f4f7f5",
              fontSize: { xs: 30, sm: 36 },
              fontWeight: 650,
              letterSpacing: "-0.035em",
              lineHeight: 1.12,
            }}
          >
            Sign in to developer tools
          </Typography>
          <Typography
            sx={{
              mt: 1.5,
              color: "#969f9a",
              fontSize: 15,
              lineHeight: 1.65,
            }}
          >
            Use the credentials issued to your approved mhome.ai developer
            account.
          </Typography>
          {flow === "callback" && clientDisplayName && (
            <Typography sx={{ mt: 1.25, color: "#737d78", fontSize: 13 }}>
              Continue to {clientDisplayName} after verification.
            </Typography>
          )}
        </Box>

        {error && <Alert severity="error">{error}</Alert>}

        {phase === "validating" && (
          <Stack spacing={2} alignItems="center">
            <CircularProgress size={32} />
            <Typography color="rgba(255,255,255,0.62)">
              Validating this login request…
            </Typography>
          </Stack>
        )}

        {phase === "pairing" && (
          <Stack component="form" spacing={2} onSubmit={verifyPairingCode}>
            <StepLabel number="1" title="Pair with Meow" />
            <Typography
              variant="body2"
              sx={{ color: "#929b96", lineHeight: 1.6 }}
            >
              Enter the 6-digit Meow pairing code printed by the CLI. This is
              not the email verification code.
            </Typography>
            <TextField
              id="developer-pairing-code"
              label="Meow pairing code"
              autoFocus
              autoComplete="one-time-code"
              inputProps={{ inputMode: "numeric", maxLength: 6 }}
              value={pairingCode}
              onChange={(event) =>
                setPairingCode(
                  event.target.value.replace(/\D/g, "").slice(0, 6)
                )
              }
              required
            />
            <DeveloperButton busy={busy}>Verify pairing code</DeveloperButton>
          </Stack>
        )}

        {phase === "credentials" && (
          <Stack component="form" spacing={2} onSubmit={submitCredentials}>
            <StepLabel number="2" title="Developer credentials" />
            <TextField
              id="developer-username"
              label="Developer username"
              type="text"
              autoComplete="username"
              InputProps={{ startAdornment: <PersonOutline sx={{ mr: 1 }} /> }}
              value={username}
              onChange={(event) => setUsername(event.target.value)}
              required
            />
            <TextField
              id="developer-password"
              label="Password"
              type="password"
              autoComplete="current-password"
              InputProps={{ startAdornment: <LockOutlined sx={{ mr: 1 }} /> }}
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              required
            />
            <DeveloperButton busy={busy}>Continue</DeveloperButton>
          </Stack>
        )}

        {(phase === "new-password" ||
          phase === "email-code" ||
          phase === "email-setup") && (
          <Stack component="form" spacing={2} onSubmit={submitChallenge}>
            <StepLabel
              number={phase === "new-password" ? "2" : "3"}
              title={
                phase === "new-password"
                  ? "Set a permanent password"
                  : phase === "email-setup"
                    ? "Set up developer email"
                    : "Verify developer email"
              }
            />
            {phase === "email-code" && (
              <Typography
                variant="body2"
                sx={{ color: "#929b96", lineHeight: 1.6 }}
              >
                Enter the 6-digit email verification code sent to{" "}
                {deliveryDestination}. This is separate from the Meow pairing
                code.
              </Typography>
            )}
            <TextField
              id={`developer-${phase}`}
              label={
                phase === "new-password"
                  ? "New password"
                  : phase === "email-setup"
                    ? "Developer email"
                    : "Email verification code"
              }
              type={phase === "new-password" ? "password" : "text"}
              autoComplete={
                phase === "new-password"
                  ? "new-password"
                  : phase === "email-setup"
                    ? "email"
                    : "one-time-code"
              }
              inputProps={
                phase === "email-code"
                  ? { inputMode: "numeric", maxLength: 6 }
                  : undefined
              }
              value={challengeValue}
              onChange={(event) =>
                setChallengeValue(
                  phase === "email-code"
                    ? event.target.value.replace(/\D/g, "").slice(0, 6)
                    : event.target.value
                )
              }
              required
            />
            <DeveloperButton busy={busy}>Verify and continue</DeveloperButton>
          </Stack>
        )}

        {phase === "delivery-error" && (
          <Stack spacing={2}>
            <StepLabel number="4" title="Deliver session to Meow" />
            <Typography
              variant="body2"
              sx={{ color: "#929b96", lineHeight: 1.6 }}
            >
              Email verification succeeded, but Meow did not receive the
              session. You can retry without signing in again.
            </Typography>
            <Button
              variant="contained"
              size="large"
              disabled={busy}
              onClick={retryDelivery}
              sx={{
                minHeight: 48,
                borderRadius: 2,
                bgcolor: "#6fdaa8",
                color: "#07120d",
                fontWeight: 650,
                boxShadow: "none",
                "&:hover": { bgcolor: "#83e6b7", boxShadow: "none" },
              }}
            >
              Retry delivery
            </Button>
          </Stack>
        )}
      </Stack>
    </DeveloperShell>
  );
}

function DeveloperButton({ busy, children }) {
  return (
    <Button
      type="submit"
      variant="contained"
      size="large"
      disabled={busy}
      endIcon={busy ? <CircularProgress size={17} /> : <ArrowForwardRounded />}
      sx={{
        minHeight: 48,
        borderRadius: 2,
        bgcolor: "#6fdaa8",
        color: "#07120d",
        fontWeight: 650,
        boxShadow: "none",
        "&:hover": { bgcolor: "#83e6b7", boxShadow: "none" },
      }}
    >
      {children}
    </Button>
  );
}

function StepLabel({ number, title }) {
  return (
    <Stack spacing={0.5}>
      <Typography
        variant="caption"
        sx={{
          color: "#6bcf9d",
          fontFamily:
            "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
          fontSize: 11,
          fontWeight: 650,
          letterSpacing: "0.1em",
        }}
      >
        Step {number}
      </Typography>
      <Typography sx={{ color: "#e9eeeb", fontSize: 18, fontWeight: 600 }}>
        {title}
      </Typography>
    </Stack>
  );
}

function DeveloperShell({ children }) {
  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "grid",
        placeItems: "center",
        px: 2,
        py: { xs: 3, sm: 6 },
        color: "#f4f7f5",
        background:
          "radial-gradient(circle at 12% 8%, rgba(45, 114, 83, 0.22), transparent 34%), #090d0b",
        backgroundImage:
          "linear-gradient(rgba(255,255,255,0.018) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.018) 1px, transparent 1px), radial-gradient(circle at 12% 8%, rgba(45, 114, 83, 0.22), transparent 34%)",
        backgroundSize: "48px 48px, 48px 48px, auto",
      }}
    >
      <Card
        elevation={0}
        sx={{
          width: "100%",
          maxWidth: 560,
          color: "inherit",
          overflow: "hidden",
          bgcolor: "#111614",
          border: "1px solid rgba(255,255,255,0.09)",
          borderRadius: "20px",
          boxShadow: "0 32px 90px rgba(0,0,0,0.46)",
          "& .MuiAlert-root": {
            borderRadius: 2,
            border: "1px solid rgba(255,255,255,0.08)",
          },
          "& .MuiInputLabel-root": { color: "#858e89" },
          "& .MuiInputLabel-root.Mui-focused": { color: "#76dbaa" },
          "& .MuiOutlinedInput-root": {
            minHeight: 54,
            color: "#f3f6f4",
            bgcolor: "#0b100e",
            borderRadius: 2,
            transition: "background-color 160ms ease, border-color 160ms ease",
            "& fieldset": { borderColor: "rgba(255,255,255,0.13)" },
            "&:hover fieldset": { borderColor: "rgba(255,255,255,0.24)" },
            "&.Mui-focused": { bgcolor: "#0d1310" },
            "&.Mui-focused fieldset": { borderColor: "#69d4a2" },
            "& input:-webkit-autofill": {
              WebkitBoxShadow: "0 0 0 100px #0b100e inset",
              WebkitTextFillColor: "#f3f6f4",
              caretColor: "#f3f6f4",
              borderRadius: "inherit",
            },
          },
        }}
      >
        <Box
          sx={{
            px: { xs: 2.5, sm: 3 },
            py: 1.75,
            bgcolor: "#0d211a",
            backgroundImage:
              "radial-gradient(circle at 0% 0%, rgba(111,218,168,0.12), transparent 52%)",
            borderBottom: "1px solid rgba(255,255,255,0.07)",
          }}
        >
          <Stack direction="row" alignItems="center" spacing={1.25}>
            <Box
              component="img"
              src="/logo_2b.svg"
              alt="mhome.ai"
              sx={{ width: 40, height: 40 }}
            />
            <Box>
              <Typography
                sx={{
                  color: "#f0f5f2",
                  fontSize: 19,
                  fontWeight: 700,
                  letterSpacing: "-0.025em",
                  lineHeight: 1.1,
                }}
              >
                mhome.ai
              </Typography>
              <Typography
                sx={{
                  mt: 0.5,
                  color: "#789f8c",
                  fontSize: 11,
                  letterSpacing: "0.025em",
                }}
              >
                Orchestrate The Entire Home
              </Typography>
            </Box>
          </Stack>
        </Box>

        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            p: { xs: 3, sm: 4.5 },
          }}
        >
          {children}
        </Box>
      </Card>
    </Box>
  );
}
